import axios from 'axios'

import { SanitySiteStrings } from '@data/sanity/queries/types/site'
import { parseShopifyCart } from '@lib/shopify/cart'
import { createShopifyCart } from '@lib/shopify/graphql/cart'
import { ShopifyClient } from '@lib/shopify/graphql/client'
import { ShopifyShop } from '@lib/shopify/graphql/shop'
import { getCheckoutUrlStorageKey } from '@lib/checkout'
import { Locale } from '@lib/language'
import {
  Cart,
  CartDiscountUpdateResponse,
  CartFormValues,
  ValidateCartProps,
} from './types'
import { ValidateVatIdResult } from '@pages/api/tax/validate-vat-id'

export const emptyCart: Cart = {
  id: '',
  lineItems: [],
  subTotal: 0,
  total: 0,
  webUrl: '',
  automaticDiscount: {
    title: '',
    amount: 0,
  },
  discountCodes: [],
}

/**
 * Validates cart form.
 */
export const validateCart = async (
  strings: SanitySiteStrings,
  values: CartFormValues
) => {
  const results: ValidateCartProps = {
    errors: {},
    vatIdCountryCode: null,
  }

  if (values.vatId) {
    const validationResult = await validateVatId(values.vatId)
    results.vatIdCountryCode = validationResult?.countryCode ?? null

    if (!validationResult?.isValid) {
      results.errors.vatId = !validationResult
        ? strings.cartVatIdError
        : strings.cartInvalidVatIdError
    }
  }

  return results
}

/**
 * Calls API route that handles cart discount updating.
 */
export const updateCartDiscount = async (
  locale: Locale,
  cartId: string
): Promise<Cart | undefined> => {
  try {
    const discountUpdateResult = await axios.get<CartDiscountUpdateResponse>(
      '/api/shopify/cart-discount-update',
      {
        params: { cart_id: cartId },
        headers: {
          'Content-Type': 'application/json',
          'X-Locale': locale,
        },
      }
    )

    if (discountUpdateResult.data?.error || !discountUpdateResult.data?.cart) {
      throw new Error(discountUpdateResult.data.error ?? 'Unknown error')
    }

    return discountUpdateResult.data.cart
  } catch (error) {
    console.log(error)
  }
}

/**
 * Gets cart ID local storage key.
 */
export const getCartIdStorageKey = (locale: Locale) => `cart_id_v2_${locale}`

/**
 * Validates VAT ID using API route.
 */
export const validateVatId = async (vatId: CartFormValues['vatId']) => {
  try {
    const validationResult = await axios.get<ValidateVatIdResult>(
      '/api/tax/validate-vat-id',
      {
        params: { id: vatId },
        headers: { 'Content-Type': 'application/json' },
      }
    )

    return validationResult.data
  } catch (_) {
    return
  }
}

/**
 * Loads cart data from Shopify.
 */
export const loadCart = async (
  locale: Locale,
  shopifyStorefrontClient: ShopifyClient,
  shop: ShopifyShop
): Promise<Cart | undefined> => {
  const shopCart = 'cart' in shop && shop.cart ? shop.cart : null

  if (shopCart) {
    const currentCart = await parseShopifyCart(locale, shopCart)

    if (currentCart) {
      return currentCart
    }
  }

  // Delete saved cart and checkout URL, if cart was not found
  localStorage.removeItem(getCartIdStorageKey(locale))
  localStorage.removeItem(getCheckoutUrlStorageKey(locale))

  // Create a new cart
  const createShopifyCartResponse = await createShopifyCart(
    shopifyStorefrontClient,
    {
      note: '',
    }
  )
  const createdCart = await parseShopifyCart(
    locale,
    createShopifyCartResponse.cart
  )

  return createdCart
}
